<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const router = useRouter();
const { currentAccount, accountScopedRoute } = useAccount();
const { isAdmin } = useAdmin();

const plan = computed(() => currentAccount.value?.kraftchat_plan);
const planKey = computed(() => plan.value?.plan_key);
// Upgrading/billing is an administrator-only action (enforced server-side
// too - see check_admin_authorization? on the checkout endpoint), so the
// promotional card is hidden for agents rather than linking to a page they
// cannot use.
const isFreePlan = computed(() => isAdmin.value && planKey.value === 'free');
const isStartPlan = computed(() => isAdmin.value && planKey.value === 'start');

const usage = computed(() => plan.value?.ai_conversations || {});
const percentage = computed(() => usage.value.percentage || 0);
const limitReached = computed(() => percentage.value >= 100);

const barColorClass = computed(() => {
  if (percentage.value >= 100) return 'bg-n-ruby-9';
  if (percentage.value >= 80) return 'bg-n-amber-9';
  return 'bg-n-brand';
});

const billingRoute = computed(() =>
  accountScopedRoute('kraftchat_billing_settings_index')
);
</script>

<template>
  <div
    v-if="isFreePlan"
    class="flex flex-col gap-2 self-stretch mx-2 mb-2 p-3 rounded-xl bg-n-alpha-1"
  >
    <span class="text-xs font-medium text-n-slate-12">
      {{ t('SIDEBAR_PLAN_CARD.FREE.TITLE') }}
    </span>
    <span class="text-xs text-n-slate-11">
      {{
        t('SIDEBAR_PLAN_CARD.FREE.USAGE', {
          used: usage.used,
          limit: usage.limit,
        })
      }}
    </span>
    <div class="rounded-full overflow-hidden h-1.5 w-full bg-n-slate-4">
      <div
        class="h-1.5 transition-all"
        :class="barColorClass"
        :style="{ width: `${Math.min(percentage, 100)}%` }"
      />
    </div>
    <span v-if="limitReached" class="text-xs text-n-ruby-11">
      {{ t('SIDEBAR_PLAN_CARD.FREE.LIMIT_REACHED') }}
    </span>
    <NextButton
      :label="t('SIDEBAR_PLAN_CARD.UPGRADE_BUTTON')"
      sm
      class="mt-1"
      @click="router.push(billingRoute)"
    />
  </div>
  <div
    v-else-if="isStartPlan"
    class="flex flex-col gap-2 self-stretch mx-2 mb-2 p-3 rounded-xl bg-n-alpha-1"
  >
    <span class="text-xs font-medium text-n-slate-12">
      {{ t('SIDEBAR_PLAN_CARD.START.TITLE') }}
    </span>
    <span class="text-xs text-n-slate-11">
      {{ t('SIDEBAR_PLAN_CARD.START.DESCRIPTION') }}
    </span>
    <NextButton
      :label="t('SIDEBAR_PLAN_CARD.UPGRADE_BUTTON')"
      sm
      class="mt-1"
      @click="router.push(billingRoute)"
    />
  </div>
  <template v-else />
</template>
